import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  PlusIcon,
  useToast,
} from '@razorpay/blade/components';
import { FormHeading } from 'components/EmployeeOnboarding/components/styles';
import { routePaths } from 'components/Routes/data';
import { OfficeLocationForm } from './components/OfficeLocationForm';
import { defaultFormData, getCreateOfficeLocationsPayload } from './utils';
import { useOfficeLocationStore } from './store/officeLocationStore';
import { CreateOfficeLocationRequest } from 'api/types/officeLocationTypes';
import { useMutation, useQueryClient } from 'react-query';
import api from 'api';
import { AppError, getAppErrorDetails } from 'utils/AppError';
import { parseError } from 'utils/Api';
import { officeLocationQueryKeys } from '../PreviewOfficeLocations/data';
import { ConfirmSaveModal } from './components/ConfirmSaveModal';

export const CreateOfficeLocations = () => {
  const [isOpenConfirmationModal, setIsOpenConfirmationModal] = useState(false);
  const toast = useToast();
  const history = useHistory();
  const forms = useOfficeLocationStore((state) => state.forms);
  const isSubmitting = useOfficeLocationStore((state) => state.isSubmitting);
  const clearAllErrors = useOfficeLocationStore((state) => state.clearAllErrors);
  const setIsSubmitting = useOfficeLocationStore((state) => state.setIsSubmitting);
  const addForm = useOfficeLocationStore((state) => state.addForm);
  const validateAllForms = useOfficeLocationStore((state) => state.validateAllForms);
  const clearState = useOfficeLocationStore((state) => state.clearState);
  const queryClient = useQueryClient();

  const formIds = Object.keys(forms);

  const {
    mutate: createOfficeLocations,
    isLoading: isCreatingOfficeLocations,
    error: createOfficeLocationsError,
  } = useMutation({
    mutationFn: (payload: CreateOfficeLocationRequest) =>
      api.officeLocation.createOfficeLocation({ officeLocations: payload }),
    onSuccess: () => {
      queryClient.invalidateQueries([officeLocationQueryKeys.GET_OFFICE_LOCATIONS_NON_PRIMARY]);
      queryClient.invalidateQueries([officeLocationQueryKeys.GET_OFFICE_LOCATIONS]);
      history.push(routePaths.officeLocation.preview);
      clearState();
    },
    onError: (error: typeof AppError, { office_locations}) => {
      const errorMessages = getAppErrorDetails(error)?.otherDetails?.jsonResponse?.extraDetails as
        | Array<{ error: string; index: number }>
        | undefined;
      const errorMessage = errorMessages?.length
        ? errorMessages?.reduce((msg, { index }) => {
            msg += `Form ${index + 1}: ${office_locations[index].office_name}${index === errorMessages?.length - 1 ? "" : ", "}`;
            return msg;
          }, 'Errors found in - ')
        : parseError(error);
      toast.show({
        content: errorMessage,
        color: 'negative',
      });
      setIsSubmitting(false);
    },
  });

  useEffect(() => {
    // Initialize with one form if no forms exist
    if (formIds.length === 0) {
      const initialId = `initial-form-${Date.now()}`;
      addForm({ id: initialId, formData: { ...defaultFormData }, errors: {} });
    }
  }, [formIds.length]);

  const handleConfirmSave = () => {
    setIsSubmitting(true);
    setIsOpenConfirmationModal(false);
    createOfficeLocations(getCreateOfficeLocationsPayload(forms));
  };

  const handleSave = async () => {
    clearAllErrors();
    const areAllFormsValid = validateAllForms();

    if (!areAllFormsValid) {
      return;
    }

    setIsOpenConfirmationModal(true);
  };

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }
    window.location.href = routePaths.phpRoutes.companyDetails;
  };

  const handleAddOfficeLocation = () => {
    const newId = `new-form-${Date.now()}`;
    addForm({ id: newId, formData: { ...defaultFormData }, errors: {} });
  };

  const formApiErrors = getAppErrorDetails(createOfficeLocationsError)?.otherDetails?.jsonResponse
    ?.extraDetails as Array<{ error: string }> | undefined;

  return (
    <>
      <Modal isOpen={true} onDismiss={handleClose} size="full">
        <ModalHeader title="Office Locations" />
        <ModalBody padding="spacing.0">
          <Box
            maxWidth="500px"
            marginX="auto"
            marginY="spacing.7"
            display="flex"
            flexDirection="column"
            gap="spacing.5">
            <FormHeading
              title="Office Location"
              description="These details are required for state-wise ESIC registration and payment processing."
            />
            {formIds.map((formId, idx) => {
              return (
                <OfficeLocationForm
                  key={formId}
                  formId={formId}
                  error={formApiErrors?.[idx]?.error}
                />
              );
            })}

            <Box>
              <Button
                icon={PlusIcon}
                iconPosition="left"
                variant="secondary"
                onClick={handleAddOfficeLocation}
                isDisabled={isSubmitting}>
                Add Office Location
              </Button>
            </Box>
          </Box>
        </ModalBody>
        <ModalFooter>
          <Box maxWidth="500px" marginX="auto">
            <Box display="flex" gap="spacing.3" justifyContent="space-between" width="100%">
              <Button variant="tertiary" onClick={handleClose} isDisabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                isLoading={isSubmitting || isCreatingOfficeLocations}>
                Save
              </Button>
            </Box>
          </Box>
        </ModalFooter>
      </Modal>
      <ConfirmSaveModal
        isOpen={isOpenConfirmationModal}
        onDismiss={() => {
          setIsOpenConfirmationModal(false);
        }}
        onConfirm={handleConfirmSave}
      />
    </>
  );
};
